import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, apiError } from "@/lib/api";
import { useSchool } from "./SchoolSite";
import { StudentStep, ParentStep, DocsStep } from "./AdmissionSteps";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, PartyPopper } from "lucide-react";

const STEPS = ["Student", "Parent & OTP", "Documents", "Review"];
const EMPTY = { student_name: "", gender: "Male", dob: "", class_applying: "", previous_school: "", aadhaar: "", parent_name: "", parent_phone: "", parent_email: "", occupation: "", address: "" };

export default function Admission() {
  const { code } = useParams();
  const { data: s } = useSchool(code);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [otp, setOtp] = useState({ sent: false, verified: false, code: "", token: "" });
  const [docs, setDocs] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reqDocs = (s?.profile?.admission_required_docs?.length ? s.profile.admission_required_docs : ["photo", "birth_cert"]);
  const DOC_NAMES = { photo: "Student photo", birth_cert: "Birth certificate", tc: "Transfer certificate", aadhaar_doc: "Aadhaar card" };

  const validate = () => {
    if (step === 0 && (!form.student_name || !form.dob || !form.class_applying)) return "Fill student name, DOB and class";
    if (step === 1 && (!form.parent_name || !form.address)) return "Fill parent name and address";
    if (step === 1 && !otp.verified) return "Please verify the mobile number with OTP";
    if (step === 2) {
      const missing = reqDocs.filter((k) => !docs[k]).map((k) => DOC_NAMES[k] || k);
      if (missing.length) return `Please upload: ${missing.join(", ")}`;
    }
    return null;
  };
  const next = () => { const err = validate(); if (err) return toast.error(err); setStep(step + 1); };

  const submit = async () => {
    setBusy(true);
    try {
      const documents = Object.fromEntries(Object.entries(docs).filter(([, v]) => v).map(([k, v]) => [k, v.file_id]));
      const { data } = await axios.post(`${API}/public/admissions`, { ...form, school_code: code, documents, verify_token: otp.token });
      setResult(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 text-center" data-testid="admission-success">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><PartyPopper size={36} /></motion.div>
        <h1 className="mt-6 text-3xl font-bold font-head sm:text-4xl">Application submitted!</h1>
        <p className="mt-3 text-slate-600">We've sent a confirmation on WhatsApp to {result.parent_phone}. Save your application number to track status.</p>
        <div className="pub-card mx-auto mt-8 inline-block px-8 py-5">
          <p className="text-xs uppercase tracking-wider text-slate-500">Application Number</p>
          <p className="mt-1 font-mono text-2xl font-bold text-indigo-700" data-testid="application-no">{result.application_no}</p>
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/track" className="pub-btn" data-testid="success-track-btn">Track Application <ArrowRight size={16} /></Link>
          <Link to={`/s/${code}`} className="pub-btn pub-btn-outline">Back to school</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pub-mesh min-h-[80vh]">
      <div className="mx-auto max-w-4xl px-5 py-12" data-testid="admission-page">
        <Link to={`/s/${code}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-700"><ArrowLeft size={15} /> {s?.name || "Back"}</Link>
        <h1 className="mt-3 text-3xl font-bold font-head sm:text-4xl">Online Admission Form</h1>
        <p className="mt-2 text-slate-600">Academic Year {s?.academic_year || ""}{s?.name ? ` · ${s.name}` : ""}</p>
        {s?.profile?.admission_intro && (
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">{s.profile.admission_intro}</p>
        )}
        {s?.profile?.admission_notice && (
          <p className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="adm-notice">
            {s.profile.admission_notice}
          </p>
        )}

        {/* Stepper */}
        <ol className="mt-8 grid grid-cols-4 gap-2" data-testid="stepper">
          {STEPS.map((l, i) => (
            <li key={l} className="flex flex-col gap-2">
              <div className={`h-1.5 rounded-full transition-colors duration-500 ${i <= step ? "bg-indigo-600" : "bg-slate-200"}`} />
              <span className={`text-xs font-medium ${i === step ? "text-indigo-700" : i < step ? "text-slate-700" : "text-slate-400"}`}>{i < step ? <CheckCircle2 size={12} className="mr-1 inline" /> : `${i + 1}. `}{l}</span>
            </li>
          ))}
        </ol>

        <div className="pub-card mt-6 p-6 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
              {step === 0 && <StudentStep form={form} upd={upd} classes={s?.classes || []} />}
              {step === 1 && <ParentStep form={form} upd={upd} otp={otp} setOtp={setOtp} />}
              {step === 2 && <DocsStep docs={docs} setDocs={setDocs} required={reqDocs} />}
              {step === 3 && <Review form={form} docs={docs} school={s} />}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
            <button className="pub-btn pub-btn-outline" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="adm-back-btn"><ArrowLeft size={15} /> Back</button>
            {step < 3
              ? <button className="pub-btn" onClick={next} data-testid="adm-next-btn">Continue <ArrowRight size={15} /></button>
              : <button className="pub-btn !bg-indigo-600" onClick={submit} disabled={busy} data-testid="adm-submit-btn">{busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Submit Application</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Review({ form, docs, school }) {
  const rows = [["School", school?.name], ["Student", form.student_name], ["Gender / DOB", `${form.gender} · ${form.dob}`], ["Class", form.class_applying], ["Previous School", form.previous_school || "—"], ["Parent", form.parent_name], ["Mobile", `${form.parent_phone} (verified)`], ["Email", form.parent_email || "—"], ["Address", form.address], ["Documents", Object.values(docs).filter(Boolean).map((d) => d.filename).join(", ")]];
  return (
    <div data-testid="review-step">
      <h3 className="font-semibold font-head text-lg">Review your application</h3>
      <dl className="mt-4 divide-y divide-slate-100 text-sm">
        {rows.map(([k, v]) => <div key={k} className="grid grid-cols-3 gap-3 py-2.5"><dt className="text-slate-500">{k}</dt><dd className="col-span-2 font-medium text-slate-800">{v}</dd></div>)}
      </dl>
      <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-xs text-amber-800">By submitting, you confirm the details are true. The school will contact you on WhatsApp for further steps.</p>
    </div>
  );
}
